import { Injectable } from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {TreatmentEndoDataModel} from '../modules/treatment/models/treatment-endo-data.model';
import {HttpClient} from '@angular/common/http';
import {TreatmentEndoPerforationDataModel} from '../modules/treatment/models/treatment-endo-perforation-data.model';
import {ToothCanalConfigurationModel} from '../models/configuration/tooth-canal-configuration.model';
import {TreatmentFracturesModel} from '../modules/treatment/models/treatment-fractures.model';
import {TreatmentEndoToothGroupDataModel, TreatmentEndoToothGroupDataTable, TreatmentEndoToothGroupModel} from '../modules/treatment/models/treatment-endo-tooth-group-data.model';
import {AnesthesiaConfigurationModel} from '../modules/treatment/models/configuration/anesthesia-configuration.model';
import {TreatmentEndoAnesthesiaDataModel} from '../modules/treatment/models/treatment-endo-anesthesia-data.model';
import {TreatmentEndoCanalDataModel} from '../modules/treatment/models/treatment-endo-canal-data.model';
import {TreatmentEndoAutofillItemModel} from '../modules/treatment/models/configuration/treatment-endo-autofill-item.model';

@Injectable({
  providedIn: 'root'
})
export class TreatmentService {

  constructor(private http: HttpClient) {
    //
  }

  // Base
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  getTreatment(patientId: string, caseWorkItemId: string): Observable<TreatmentEndoDataModel> {
    return this.http.get<TreatmentEndoDataModel>(`api/treatment/get-treatment/${patientId}/${caseWorkItemId}`);
  }


  saveTreatment(treatmentModel: TreatmentEndoDataModel): Observable<TreatmentEndoDataModel> {
    return this.http.post<TreatmentEndoDataModel>(`api/treatment/save-treatment`, treatmentModel);
  }

  // Perforations
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  getTreatmentPerforation(id: string): Observable<TreatmentEndoPerforationDataModel> {
    return this.http.get<TreatmentEndoPerforationDataModel>(`api/treatment/get-treatment-perforation/${id}`);
  }

  saveTreatmentPerforation(perforationModel: TreatmentEndoPerforationDataModel): Observable<TreatmentEndoPerforationDataModel> {
    return this.http.post<TreatmentEndoPerforationDataModel>('api/treatment/save-treatment-perforation', perforationModel);
  }

  // Canals
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  getCanals(treatmentId: string): Observable<TreatmentEndoCanalDataModel[]> {
    return this.http.get<TreatmentEndoCanalDataModel[]>(`api/treatment/get-treatment-canals/${treatmentId}`);
  }

  getTreatmentCanalByGroup(groupId: string): Observable<TreatmentEndoDataModel> {
    return this.http.get<TreatmentEndoDataModel>(`api/treatment/get-treatmentCanalByGroupId/${groupId}`);
  }

  getTreatmentCanalGroupByTreatmentID(treatmentId: string): Observable<string> {
    return this.http.get<string>(`api/treatment/get-treatmentCanalGroupByTreatmentID/${treatmentId}`);
  }

  saveCanalItem(canal: TreatmentEndoCanalDataModel): Observable<TreatmentEndoCanalDataModel> {
    return this.http.post<TreatmentEndoCanalDataModel>('api/treatment/save-treatment-canal', canal);
  }

  deleteCanalItem(canal: TreatmentEndoCanalDataModel): Observable<any> {
    return this.http.get<any>(`api/treatment/delete-treatment-canal/${canal.TreatmentCanalID}`);
  }

  // Fractures
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  getFractures(caseWorkItemToothId: string): Observable<TreatmentFracturesModel[]> {
    return this.http.get<TreatmentFracturesModel[]>(`api/treatment/get-fractures/${caseWorkItemToothId}`);
  }

  saveFracture(fractureModel: TreatmentFracturesModel): Observable<TreatmentFracturesModel> {
    return this.http.post<TreatmentFracturesModel>('api/treatment/save-fracture', fractureModel);
  }

  // Tooth Grouping
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  getToothGroup( CaseWorkItemId: string, patientId: string): Observable<TreatmentEndoToothGroupDataTable[]> {
    return this.http.get<TreatmentEndoToothGroupDataTable[]>(`api/treatment/get-tooth-group/${CaseWorkItemId}/${patientId}`);
  }

  deleteToothGroup(group: TreatmentEndoToothGroupDataModel): Observable<TreatmentEndoToothGroupDataModel> {
    return this.http.post<TreatmentEndoToothGroupDataModel>('api/treatment/delete-tooth-group', group);
  }

  saveToothGroup(group: TreatmentEndoToothGroupDataModel): Observable<TreatmentEndoToothGroupModel> {
    return this.http.post<TreatmentEndoToothGroupModel>('api/treatment/save-tooth-group', group);
  }

  getTreatmentCanalTeethByGroup(groupId: string, patientId: string): Observable<any> {
    return this.http.get<any>(`api/treatment/get-treatmentCanalTeethByGroup/${groupId}/${patientId}`);
  }

  // Anesthesia
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  getAnesthesia(patientId: string): Observable<TreatmentEndoAnesthesiaDataModel[]> {
    return this.http.get<TreatmentEndoAnesthesiaDataModel[]>(`api/treatment/get-anesthesia/${patientId}`);
  }

  saveAnesthesiaItem(model: TreatmentEndoAnesthesiaDataModel): Observable<TreatmentEndoAnesthesiaDataModel> {
    return this.http.post<TreatmentEndoAnesthesiaDataModel>('api/treatment/save-anesthesia-item', model);
  }

  deleteAnesthesiaItem(anesthesiaId: string): Observable<any> {
    return this.http.get<any>(`api/treatment/delete-anesthesia-item/${anesthesiaId}`);
  }

  // Configuration
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  getTreatmentAutoFillConfigurationModels(): Observable<TreatmentEndoAutofillItemModel[]> {
    return this.http.get<TreatmentEndoAutofillItemModel[]>('api/treatment/get-treatment-autofill-configuration-models');
  }

  saveTreatmentAutoFillConfiguration(models: TreatmentEndoAutofillItemModel[]): Observable<any> {
    return this.http.post<any>('api/treatment/save-treatment-autofill-configuration', models);
  }

  getCanalConfigurationModels(): Observable<ToothCanalConfigurationModel[]> {
    return this.http.get<ToothCanalConfigurationModel[]>('api/treatment/get-canal-configuration-models');
  }

  getAnesthesiaConfigurationModels(): Observable<AnesthesiaConfigurationModel> {
    return this.http.get<AnesthesiaConfigurationModel>('api/treatment/get-anesthesia-configuration-models');
  }

  // Utils
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  getToothNumber(treatmentModel: TreatmentEndoDataModel, caseWorkItemToothId: string): string {
    if (treatmentModel?.cfgTeeth?.length === 1) {
      return treatmentModel.cfgTeeth[0].cfgToothDescriptions[0].Notation;
    } else {
      return '';
  }
}

  getTreatmentPerforations(treatmentId: string) {
    return this.http.get<TreatmentEndoPerforationDataModel[]>(`api/treatment/get-treatment-perforations/${treatmentId}`);
  }
}
